import { useCallback, useMemo } from 'react'

import { LabelType, Review } from '@skop-app/lib-domain/types'
import { ReviewType } from '@skop-app/lib-types'
import { useTranslations } from 'next-intl'

import { useReviewData } from '@/context'
import { useUserContext } from '@/context'
import { useProjects } from '@/services/queries/projects'
import { usePropertiesOptions } from '@/services/property/usePropertiesOptions'
import { buildReviewDrawerData, getReviewFieldKey } from '@/utils/reviews'
import { ReviewDrawerElement } from '@/types'

export type ReviewsElement = ReviewDrawerElement

export type ReviewsFiltersType = {
  activeTab: 'allComments' | 'forYou'
  activeStatus: 'open' | 'closed' | 'allStatuses'
  activeProject: string
}

const BUILTIN_REVIEW_FIELD_TRANSLATION_KEYS: Record<string, string> = {
  comments: 'commentsField',
  label: 'labelField',
  value: 'valueField',
}

export const useReviewsData = (filters: ReviewsFiltersType) => {
  const t = useTranslations('Review')
  const { user } = useUserContext()
  const { allReviews = [], isLoading } = useReviewData()
  const { supportedKeysByKey } = usePropertiesOptions()
  const { data: projectsData = [] } = useProjects()

  const projectLabelsById = useMemo(
    () =>
      Object.fromEntries(
        projectsData.map((project) => [project.value, project.label]),
      ) as Record<string, string>,
    [projectsData],
  )

  const resolveProjectLabel = useCallback(
    (projectID: string) => projectLabelsById[projectID],
    [projectLabelsById],
  )

  const resolveFieldLabel = useCallback(
    (review: Review): string | undefined => {
      const fieldKey = getReviewFieldKey(review)
      if (!fieldKey) return undefined

      const propertyLabel = supportedKeysByKey[fieldKey]?.label
      if (propertyLabel) return propertyLabel

      const translationKey = BUILTIN_REVIEW_FIELD_TRANSLATION_KEYS[fieldKey]
      return translationKey ? t(translationKey) : undefined
    },
    [supportedKeysByKey, t],
  )

  const resolveReviewTypeLabel = useCallback(
    (type: Review['type']) => {
      switch (type) {
        case ReviewType.REVIEW_PRODUCT:
          return t('typeProduct')
        case ReviewType.REVIEW_REFERENCE:
          return t('typeReference')
        case ReviewType.REVIEW_OPTION:
          return t('typeOption')
        case ReviewType.REVIEW_HEADER:
          return t('typeHeader')
        default:
          return t('typeUnknown')
      }
    },
    [t],
  )

  const drawerData = useMemo(
    () =>
      buildReviewDrawerData({
        reviews: allReviews,
        filters,
        currentUserId: user?.id,
        resolveFieldLabel,
        resolveProjectLabel,
        resolveReviewTypeLabel,
      }),
    [
      allReviews,
      filters,
      resolveFieldLabel,
      resolveProjectLabel,
      resolveReviewTypeLabel,
      user?.id,
    ],
  )

  return {
    isLoading,
    projects: drawerData.projects as LabelType[],
    filteredData: drawerData.elements,
  }
}
